import { createWithEqualityFn } from 'zustand/traditional'
import { subscribeWithSelector } from 'zustand/middleware'
import { mutateRule } from '../utils/MutateDriverMath'
import { mockXGBoostProcessScenarios } from '../utils/LocalXGBoost'
import { getScenarioColor } from './styles/BuildScenarioStore.styles'

export const MAX_ALLOWED_SLIDE_DIFF_PERCENT = 1 // 10% difference allowed for slider inputs

// Remote model API throttle configuration
export const REMOTE_MODEL_API_THROTTLE_MS = 100 // Configurable throttle frequency in milliseconds

/**
 * Build Scenario Store
 *
 * This Zustand store manages the state for the build scenario functionality, including:
 * - Model and driver selection
 * - Scenario creation and management
 * - Driver observation data (original and mutated)
 * - Derived state handling with automatic mutation system
 *
 * The store implements a reactive mutation system that automatically processes
 * observation data when conditions are met (model + drivers + scenarios + data).
 */
const useBuildScenarioStore = createWithEqualityFn()(
  subscribeWithSelector((set, get) => ({
    // ==================== STATE DEFINITIONS ====================

    // Model and drivers data
    selectedModel: null, // Currently selected model object
    selectedDrivers: [], // Array of drivers selected for scenario analysis
    drivers: [], // All available drivers for the selected model

    // Date range from selected model
    minDate: null, // Model's timeframe start date
    maxDate: null, // Model's timeframe end date

    // Scenarios data
    scenarios: [], // Array of created scenarios with their configurations
    nextScenarioId: 1, // Auto-incrementing ID for new scenarios

    // Independent drivers observations data
    // Format: [{driverId: <int>, observationValues: <number[]>, observationTimecodes: <Date[]>}, ...]
    independentDriversOriginalObservations: [], // Original observation data (immutable baseline)
    // Note: independentDriversMutatedObservations is now stored per scenario

    // Refresh ID for tracking mutations - increments when observations are mutated
    // This enables components to reactively update when mutation processing completes
    mutatedObservationsRefreshId: 0,

    // Flag to track if scenarios have been modified and are ready for submission
    isReadyForSubmitFlag: false,

    // Remote model output data
    remoteModelOutput: null, // Results from remote XGBoost API
    lastRemoteModelOutput: null, // Persisted copy of last successful remote model output
    remoteModelLoading: false, // Loading state for remote model API calls
    remoteModelError: null, // Error state for remote model API calls

    // Initial clean model output - baseline prediction with original observation data
    initialCleanModelOutput: null, // Results from processing original observations (no mutations)
    initialCleanModelLoading: false, // Loading state for initial clean model processing
    initialCleanModelError: null, // Error state for initial clean model processing

    // ==================== MODEL & DRIVER ACTIONS ====================

    /**
     * Resets all store state to initial values
     * Used when starting fresh or switching models
     */
    reset: () => {
      set({
        selectedModel: null,
        selectedDrivers: [],
        drivers: [],
        minDate: null,
        maxDate: null,
        scenarios: [],
        nextScenarioId: 1,
        independentDriversOriginalObservations: [],
        mutatedObservationsRefreshId: 0,
        isReadyForSubmitFlag: false,
        remoteModelOutput: null,
        lastRemoteModelOutput: null,
        remoteModelLoading: false,
        remoteModelError: null,
        initialCleanModelOutput: null,
        initialCleanModelLoading: false,
        initialCleanModelError: null,
      })
    },

    /**
     * Fetches driver observation data from the backend API
     * This function handles the decision between using real API or dummy data
     *
     * @param {number[]} driverIds - Array of driver IDs to fetch data for
     * @param {Date|string} minDate - Start date for the observation period
     * @param {Date|string} maxDate - End date for the observation period
     * @returns {Promise<Array>} Promise resolving to array of driver observation objects
     *
     **/
    getDriverObservationData: async (modelId, minDate, maxDate) => {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/artifacts/${modelId}/drivers`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        },
      )

      if (!response.ok) {
        throw new Error(`API Error: ${response.status} ${response.statusText}`)
      }
      //You might have to wash the data here into another form
      //* Expected output from this function is:
      //  [
      //    {
      //      driverId: number,                    // Driver ID
      //      observationValues: number[],         // Array of numeric observation values
      //      observationTimecodes: string[]       // Array of ISO date strings (YYYY-MM-DD format)
      //    },
      //    ...
      //  ]
      const result = await response.json()
      return result.data.map((elem) => {
        return {
          driverId: elem.id,
          observationValues: elem.records.map((item) => item.value),
          observationTimecodes: elem.records.map((item) => item.date),
        }
      })
    },

    /**
     * Sets the selected model and resets all dependent state
     * This is the primary entry point for starting a new scenario build session
     * Automatically sets drivers if the model has independent_drivers
     *
     * @param {Object} model - The selected model object with timeframe information
     */
    setSelectedModel: async (model) => {
      // Reset all state first
      get().reset()

      // Set model-specific state
      set({
        selectedModel: model,
        minDate: model?.timeframe?.from || null,
        maxDate: model?.timeframe?.to || null,
      })

      // Automatically set drivers if model has independent_drivers
      if (model && model.independent_drivers) {
        await get().setDrivers(model.id, model.independent_drivers)
      }
    },

    /**
     * Sets the currently selected drivers for scenario analysis
     * Only selected drivers will have their observations mutated by the reactive system
     *
     * @param {Array} drivers - Array of driver IDs to be used in scenarios
     */
    setSelectedDrivers: (drivers) => {
      set({ selectedDrivers: drivers })
    },

    /**
     * Sets the available drivers and fetches their observation data from the API
     * This triggers the loading of baseline observation data for all drivers
     *
     * @param {Array} drivers - Array of all available driver objects
     */
    setDrivers: async (modelId, drivers) => {
      // Get current state to access model date range
      const state = get()

      try {
        // Fetch observation data from API (or dummy implementation)
        const observationData = await get().getDriverObservationData(
          modelId,
          state.minDate,
          state.maxDate,
        )

        // Convert API response format to internal format
        const formattedObservations = observationData.map((obs) => ({
          driverId: obs.driverId,
          observationValues: obs.observationValues,
          observationTimecodes: obs.observationTimecodes.map(
            (dateStr) => new Date(dateStr),
          ),
        }))

        set({
          drivers,
          independentDriversOriginalObservations: formattedObservations,
        })
      } catch (error) {
        console.error('Failed to fetch driver observation data:', error)

        // Fallback to empty observations on error
        // In production, you might want to show an error message to the user
        set({
          drivers,
          independentDriversOriginalObservations: [],
        })

        // Re-throw error so calling code can handle it
        throw error
      }
    },

    // ==================== OBSERVATION DATA ACTIONS ====================

    /**
     * Manually sets the original observation data (bypasses dummy data generation)
     * Used when loading real observation data from external sources
     *
     * @param {Array} observations - Array of observation objects with driverId, values, and timecodes
     */
    setIndependentDriversOriginalObservations: (observations) => {
      set({ independentDriversOriginalObservations: observations })
    },

    /**
     * Updates a specific driver's mutated observations within a specific scenario
     * Used for direct control over driver-specific mutated data state
     *
     * @param {number} scenarioId - ID of the scenario to update
     * @param {number} driverId - ID of the driver to update
     * @param {Object} mutatedObservations - Object with observationValues and observationTimecodes arrays
     */
    setDriverMutatedObservations: (
      scenarioId,
      driverId,
      mutatedObservations,
    ) => {
      set((state) => ({
        scenarios: state.scenarios.map((scenario) =>
          scenario.id === scenarioId
            ? {
                ...scenario,
                driverValues: scenario.driverValues.map((driver) =>
                  driver.id === driverId
                    ? { ...driver, mutatedObservations }
                    : driver,
                ),
              }
            : scenario,
        ),
      }))
    },

    /**
     * Gets the default start and end observation values for a driver
     * Extracts the logic for finding original observation data and calculating default values
     *
     * @param {number} driverId - The driver ID to get defaults for
     * @returns {Object} Object with observationValueStart and observationValueEnd
     */
    getDriverDefaultValues: (driverId) => {
      const state = get()
      // Find the original observation data for this driver
      const originalObservation =
        state.independentDriversOriginalObservations.find(
          (obs) => obs.driverId === driverId,
        )

      // Get actual start and end values from original observations
      let observationValueStart
      let observationValueEnd

      if (
        originalObservation &&
        originalObservation.observationValues.length > 0
      ) {
        observationValueStart = originalObservation.observationValues[0] // First value
        observationValueEnd =
          originalObservation.observationValues[
            originalObservation.observationValues.length - 1
          ] // Last value
      }

      return {
        observationValueStart,
        observationValueEnd,
      }
    },

    /**
     * Calculates the min, max, and step values for driver inputs based on observation data
     * This uses the full range of observation values to determine appropriate bounds
     *
     * @param {number} driverId - The driver ID to calculate bounds for
     * @returns {Object} Object with minValue, maxValue, and steps
     */
    calculateDriverInputBounds: (driverId) => {
      const state = get()

      // Find the original observation data for this driver
      const originalObservation =
        state.independentDriversOriginalObservations.find(
          (obs) => obs.driverId === driverId,
        )

      if (
        !originalObservation ||
        !originalObservation.observationValues.length
      ) {
        // Fallback values if no data available
        return {
          minValue: 0,
          maxValue: 100,
          steps: 1,
        }
      }

      const observationValues = originalObservation.observationValues
      const observationValueEnd =
        observationValues[observationValues.length - 1]

      // Find max and min values from the whole observation list
      const A = Math.max(...observationValues)
      const B = Math.min(...observationValues)

      // Calculate distances
      const A_dist = Math.abs(A - observationValueEnd)
      const B_dist = Math.abs(observationValueEnd - B)

      // Find the maximum distance
      const C = Math.max(A_dist, B_dist)

      // Calculate bounds using the new formula
      const minValue = observationValueEnd - C * MAX_ALLOWED_SLIDE_DIFF_PERCENT
      const maxValue = observationValueEnd + C * MAX_ALLOWED_SLIDE_DIFF_PERCENT
      const steps = (maxValue - minValue) / 100

      return {
        minValue,
        maxValue,
        steps,
      }
    },

    // ==================== SCENARIO MANAGEMENT ACTIONS ====================

    /**
     * Creates a new scenario with default values
     * Requires a selected model to determine timeframe and available drivers
     *
     * @returns {Object} The newly created scenario object
     * @throws {Error} If no model is selected
     */
    createScenario: () => {
      const { selectedModel, drivers, nextScenarioId } = get()

      if (!selectedModel) {
        return null
      }

      const newScenario = {
        id: nextScenarioId,
        name: `Scenario ${nextScenarioId}`,
        color: getScenarioColor(nextScenarioId), // Assign color based on scenario ID
        isNew: true, // Flag for UI highlighting
        startDate: get().getInitStartDate(),
        driverValues: drivers.map((driver) => {
          // Get default values using helper function
          const { observationValueStart, observationValueEnd } =
            get().getDriverDefaultValues(driver.id)

          return {
            id: driver.id,
            value: observationValueEnd,
            observationValueStart: observationValueStart,
            observationValueEnd: observationValueEnd,
            mutatedObservations: {
              observationValues: [],
              observationTimecodes: [],
            },
          }
        }),
        // Note: independentDriversMutatedObservations is now embedded within each driver
      }

      set((state) => ({
        scenarios: [...state.scenarios, newScenario],
        nextScenarioId: nextScenarioId + 1,
      }))

      return newScenario
    },

    /**
     * Removes the "new" flag from a scenario (used for UI state management)
     *
     * @param {number} scenarioId - ID of the scenario to update
     */
    makeScenarioNotFresh: (scenarioId) => {
      set((state) => ({
        scenarios: state.scenarios.map((scenario) =>
          scenario.id === scenarioId ? { ...scenario, isNew: false } : scenario,
        ),
      }))
    },

    /**
     * Updates the name of a specific scenario
     *
     * @param {number} scenarioId - ID of the scenario to update
     * @param {string} name - New name for the scenario
     */
    setScenarioName: (scenarioId, name) => {
      set((state) => ({
        scenarios: state.scenarios.map((scenario) =>
          scenario.id === scenarioId ? { ...scenario, name } : scenario,
        ),
      }))
    },

    /**
     * Updates the start date of a specific scenario
     *
     * @param {number} scenarioId - ID of the scenario to update
     * @param {Date} date - New start date for the scenario
     */
    setScenarioStartDate: (scenarioId, date) => {
      set((state) => ({
        scenarios: state.scenarios.map((scenario) =>
          scenario.id === scenarioId
            ? { ...scenario, startDate: date }
            : scenario,
        ),
      }))
    },

    /**
     * Updates a specific driver's value within a specific scenario
     * This change will trigger the reactive mutation system
     *
     * @param {number} scenarioId - ID of the scenario to update
     * @param {number} driverId - ID of the driver to update
     * @param {number} value - New value for the driver in this scenario
     */
    setScenarioDriverValue: (scenarioId, driverId, value) => {
      set((state) => ({
        scenarios: state.scenarios.map((scenario) =>
          scenario.id === scenarioId
            ? {
                ...scenario,
                driverValues: scenario.driverValues.map((driver) =>
                  driver.id === driverId
                    ? { ...driver, value } // Preserve mutatedObservations
                    : driver,
                ),
              }
            : scenario,
        ),
      }))
    },

    /**
     * Removes a scenario from the store
     *
     * @param {number} scenarioId - ID of the scenario to delete
     */
    deleteScenario: (scenarioId) => {
      set((state) => ({
        scenarios: state.scenarios.filter(
          (scenario) => scenario.id !== scenarioId,
        ),
      }))
    },

    /**
     * Sets the isReadyForSubmitFlag instantly without any checks or side effects
     *
     * @param {boolean} value - The value to set for isReadyForSubmitFlag
     */
    setReadyForSubmitFlagInstantly: (value) => {
      set({ isReadyForSubmitFlag: value })
    },

    /**
     * Universal scenario data processing function
     * This wrapper chooses between mock local XGBoost or real API based on configuration
     *
     * @param {string} modelId - The ID of the model to use for predictions
     * @param {Array} observationData - Array of observation data (original or mutated)
     * @returns {Promise<Object>} Promise resolving to the API response
     *
     *
     **/
    processScenarioData: async (modelId, observationData) => {
      const USE_MOCK_API = false

      const groupedData = Object.values(
        observationData.reduce((acc, item) => {
          const key = item.scenarioId
          if (!acc[key]) {
            acc[key] = []
          }
          acc[key].push(item)
          return acc
        }, {}),
      )

      if (USE_MOCK_API) {
        return await mockXGBoostProcessScenarios(modelId, observationData)
      } else {
        const scenarios = []
        for (const data of groupedData) {
          // All items in `data` have the same ID, so we just grab it from the first one.
          const scenarioId = data[0]?.scenarioId

          const response = await fetch(
            `${process.env.REACT_APP_API_URL}/api/scenarios/`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${localStorage.getItem('token')}`,
              },
              body: JSON.stringify({
                artifact_id: modelId,
                drivers: data.map((elem) => {
                  return {
                    id: elem.driverId,
                    data: elem.observationTimecodes.map((date, i) => ({
                      date,
                      value: elem.observationValues[i],
                    })),
                  }
                }),
              }),
            },
          )

          if (!response.ok) {
            let errorMessage = `API Error: ${response.status} ${response.statusText}`
            try {
              const text = await response.text()
              try {
                const errorData = JSON.parse(text)
                if (errorData.message) {
                  const msg =
                    typeof errorData.message === 'object'
                      ? JSON.stringify(errorData.message)
                      : errorData.message
                  errorMessage = `${response.status}: ${msg}`
                }
              } catch (e) {
                // If response is not JSON (e.g. HTML 500), use text or default message
                if (text && text.length < 200) errorMessage += ` - ${text}`
              }
            } catch (e) {
              // Failed to read text, keep default message
            }
            throw new Error(errorMessage)
          }

          const result = await response.json()
          const plot = result.data?.plot

          if (scenarioId !== undefined && plot) {
            scenarios.push({
              scenarioId: scenarioId,
              observationValues: plot.y,
              observationTimecodes: plot.x,
            })
          }
        }
        return {
          success: true,
          scenarios,
        }
      }
    },

    /**
     * Processes initial clean model output using original observation data
     * This provides a baseline prediction without any scenario mutations
     *
     * @returns {Promise<Object>} Promise resolving to the initial clean model output
     */
    getInitialCleanModelOutput: async () => {
      const state = get()

      // Validate that we have required data
      if (!state.selectedModel?.id) {
        const error = 'No model selected for initial clean processing'
        set({ initialCleanModelError: error, initialCleanModelLoading: false })
        throw new Error(error)
      }

      if (
        !state.independentDriversOriginalObservations ||
        state.independentDriversOriginalObservations.length === 0
      ) {
        const error =
          'No original observation data available for initial clean processing'
        set({ initialCleanModelError: error, initialCleanModelLoading: false })
        throw new Error(error)
      }

      // Set loading state
      set({
        initialCleanModelLoading: true,
        initialCleanModelError: null,
        initialCleanModelOutput: null,
      })

      try {
        // Create a single "clean" scenario using original observation data
        const cleanObservationData =
          state.independentDriversOriginalObservations.map((obs) => ({
            scenarioId: 0, // Use ID 0 for the clean baseline scenario
            driverId: obs.driverId,
            observationValues: obs.observationValues,
            observationTimecodes: obs.observationTimecodes.map((timecode) => {
              // Ensure proper date formatting
              const date = new Date(timecode)
              return date.toISOString().split('T')[0]
            }),
          }))

        // Use the universal processing function
        const result = await get().processScenarioData(
          state.selectedModel.id,
          cleanObservationData,
        )

        // Update state with successful result
        set({
          initialCleanModelOutput: result,
          initialCleanModelLoading: false,
          initialCleanModelError: null,
        })

        return result
      } catch (error) {
        console.error('❌ Initial clean model processing error:', error)
        set({
          initialCleanModelError: error.message,
          initialCleanModelLoading: false,
          initialCleanModelOutput: null,
        })
        throw error
      }
    },

    /**
     * Calls remote model API with mutated observation data
     * This function is called after mutation is complete to get model predictions
     *
     * @returns {Promise<Object>} Promise resolving to the API response
     */
    getRemoteModelOutput: async () => {
      const state = get()

      // Validate that we have required data
      if (!state.selectedModel?.id) {
        const error = 'No model selected for remote processing'
        set({ remoteModelError: error, remoteModelLoading: false })
        throw new Error(error)
      }

      if (!state.scenarios || state.scenarios.length === 0) {
        const error = 'No scenarios available for remote processing'
        set({ remoteModelError: error, remoteModelLoading: false })
        throw new Error(error)
      }

      // Set loading state
      set({
        remoteModelLoading: true,
        remoteModelError: null,
        remoteModelOutput: null,
      })

      try {
        // Collect all mutated observations from all scenarios
        const allMutatedObservations = []

        state.scenarios.forEach((scenario) => {
          scenario.driverValues.forEach((driverValue) => {
            if (
              driverValue.mutatedObservations &&
              driverValue.mutatedObservations.observationValues.length > 0
            ) {
              // Convert timestamps to YYYY-MM-DD format for LocalXGBoost
              const formattedTimecodes =
                driverValue.mutatedObservations.observationTimecodes.map(
                  (timecode) => {
                    const date = new Date(timecode)
                    return date.toISOString().split('T')[0] // Convert to YYYY-MM-DD
                  },
                )

              allMutatedObservations.push({
                scenarioId: scenario.id,
                driverId: driverValue.id,
                observationValues:
                  driverValue.mutatedObservations.observationValues,
                observationTimecodes: formattedTimecodes,
              })
            }
          })
        })

        if (allMutatedObservations.length === 0) {
          throw new Error(
            'No mutated observation data available for processing',
          )
        }

        // Use the universal processing function
        const result = await get().processScenarioData(
          state.selectedModel.id,
          allMutatedObservations,
        )

        // Update state with successful result
        set({
          remoteModelOutput: result,
          lastRemoteModelOutput: result, // Persist successful result
          remoteModelLoading: false,
          remoteModelError: null,
        })

        return result
      } catch (error) {
        console.error('❌ Remote model API error:', error)

        set({
          remoteModelOutput: null,
          remoteModelLoading: false,
          remoteModelError: error.message || 'Unknown error occurred',
        })

        throw error
      }
    },

    /**
     * Resets all scenario tuning data to default values
     * Useful for starting fresh with existing scenarios
     */
    resetScenarioTuningData: () => {
      const state = get()

      // Reset start dates for all scenarios
      set((prevState) => ({
        scenarios: prevState.scenarios.map((scenario) => ({
          ...scenario,
          startDate: get().getInitStartDate(),
        })),
      }))

      // Reset each driver value in each scenario using the refresh function
      state.scenarios.forEach((scenario) => {
        scenario.driverValues.forEach((driver) => {
          get().refreshScenarioDriverValue(scenario.id, driver.id)
        })
      })
    },

    /**
     * Refreshes a specific driver value in a scenario back to its original default value
     * Resets both the current value and mutated observations for that driver
     *
     * @param {number} scenarioId - The ID of the scenario to refresh
     * @param {number} driverId - The ID of the driver to refresh
     */
    refreshScenarioDriverValue: (scenarioId, driverId) => {
      set((state) => {
        const updatedScenarios = state.scenarios.map((scenario) => {
          if (scenario.id === scenarioId) {
            const updatedDriverValues = scenario.driverValues.map((driver) => {
              if (driver.id === driverId) {
                // Get default values using helper function
                const { observationValueStart, observationValueEnd } =
                  get().getDriverDefaultValues(driver.id)

                return {
                  ...driver,
                  value: observationValueEnd, // Reset to default value
                  observationValueStart: observationValueStart,
                  observationValueEnd: observationValueEnd,
                  mutatedObservations: {
                    observationValues: [],
                    observationTimecodes: [],
                  }, // Reset mutated observations
                }
              }
              return driver
            })

            return {
              ...scenario,
              driverValues: updatedDriverValues,
            }
          }
          return scenario
        })

        return {
          scenarios: updatedScenarios,
        }
      })
    },

    /**
     * Creates a snapshot of the current store state for serialization/persistence
     *
     * @returns {Object} Serializable snapshot of the current state
     */
    getSnapshot: () => {
      const state = get()
      return {
        selectedModel: state.selectedModel,
        selectedDrivers: state.selectedDrivers,
        drivers: state.drivers,
        minDate: state.minDate,
        maxDate: state.maxDate,
        scenarios: state.scenarios.map((scenario) => ({
          id: scenario.id,
          name: scenario.name,
          isNew: scenario.isNew,
          startDate: scenario.startDate,
          color: scenario.color,
          driverValues: scenario.driverValues.map((driver) => ({
            id: driver.id,
            value: driver.value,
            observationValueStart: driver.observationValueStart,
            observationValueEnd: driver.observationValueEnd,
            mutatedObservations: driver.mutatedObservations || {
              observationValues: [],
              observationTimecodes: [],
            },
          })),
        })),
        nextScenarioId: state.nextScenarioId,
        independentDriversOriginalObservations:
          state.independentDriversOriginalObservations,
        mutatedObservationsRefreshId: state.mutatedObservationsRefreshId,
        isReadyForSubmitFlag: state.isReadyForSubmitFlag,
        remoteModelOutput: state.remoteModelOutput,
        lastRemoteModelOutput: state.lastRemoteModelOutput,
        remoteModelLoading: state.remoteModelLoading,
        remoteModelError: state.remoteModelError,
        initialCleanModelOutput: state.initialCleanModelOutput,
        initialCleanModelLoading: state.initialCleanModelLoading,
        initialCleanModelError: state.initialCleanModelError,
      }
    },

    /**
     * Prefills the store state from a snapshot without triggering individual setter functions
     * This is used for restoring state when navigating back to the page
     *
     * @param {Object} snapshot - The snapshot object to restore from
     */
    prefill: (snapshot) => {
      if (!snapshot) return

      set((state) => ({
        ...state,
        selectedModel: snapshot.selectedModel || null,
        selectedDrivers: snapshot.selectedDrivers || [],
        drivers: snapshot.drivers || [],
        minDate: snapshot.minDate || null,
        maxDate: snapshot.maxDate || null,
        scenarios: snapshot.scenarios || [],
        nextScenarioId: snapshot.nextScenarioId || 1,
        independentDriversOriginalObservations:
          snapshot.independentDriversOriginalObservations || [],
        mutatedObservationsRefreshId:
          snapshot.mutatedObservationsRefreshId || 0,
        remoteModelOutput: snapshot.remoteModelOutput || null,
        lastRemoteModelOutput: snapshot.lastRemoteModelOutput || null,
        remoteModelLoading: snapshot.remoteModelLoading || false,
        remoteModelError: snapshot.remoteModelError || null,
        initialCleanModelOutput: snapshot.initialCleanModelOutput || null,
        initialCleanModelLoading: snapshot.initialCleanModelLoading || false,
        initialCleanModelError: snapshot.initialCleanModelError || null,
      }))
    },

    /**
     * Gets the initial start date for scenarios from the selected model
     *
     * @returns {Date|null} The initial start date from the selected model's timeframe
     */
    getInitStartDate: () => {
      const state = get()
      return state.selectedModel?.timeframe?.from
        ? new Date(state.selectedModel.timeframe.from)
        : null
    },
  })),
)

// ==================== HELPER FUNCTIONS ====================

/**
 * Applies scenario-specific mutation rule to observation values
 * Uses the new mathematical model from MutateDriverMath.js
 *
 * @param {Array} observationValues - Array of numeric observation values
 * @param {Array} observationTimecodes - Array of corresponding dates
 * @param {number} target - The driver's target value at t_end
 * @param {Date} t_break - The date where the break/shift begins
 * @param {Date} t_start - The earliest date in the range
 * @param {Date} t_end - The latest date in the range
 * @param {number} x_start - The initial observation value
 * @param {number} x_end - The final observation value at end
 * @returns {Array} Array of mutated observation values
 */
const mutateObservationRule = (
  observationValues,
  observationTimecodes,
  target,
  t_break,
  t_start,
  t_end,
  x_start,
  x_end,
) => {
  return mutateRule(
    target,
    t_break,
    t_start,
    t_end,
    x_start,
    x_end,
    observationValues,
    observationTimecodes,
  )
}

/**
 * Core mutation function that processes observation data for each scenario individually
 *
 * This function implements the reactive mutation system:
 * 1. Always starts from original (immutable) observation data
 * 2. Applies mutations based on each scenario's specific driver values
 * 3. Creates driver-specific mutated observations within each scenario
 * 4. Updates all scenarios and increments refresh ID
 *
 * The function processes each scenario and driver independently, allowing different
 * mutation effects based on individual scenario configurations.
 */
const mutateObservations = () => {
  const state = useBuildScenarioStore.getState()

  // Early return if no original data exists
  if (
    !state.independentDriversOriginalObservations ||
    state.independentDriversOriginalObservations.length === 0
  ) {
    return
  }

  // Create a Set for O(1) lookup performance of selected driver IDs
  // selectedDrivers already contains the IDs directly
  const selectedDriverIds = new Set(state.selectedDrivers)

  // Process each scenario individually
  const updatedScenarios = state.scenarios.map((scenario) => {
    // Update each driver's mutated observations within this scenario
    const updatedDriverValues = scenario.driverValues.map((driverValue) => {
      // Find the original observation data for this driver
      const originalObservation =
        state.independentDriversOriginalObservations.find(
          (obs) => obs.driverId === driverValue.id,
        )

      if (!originalObservation) {
        return driverValue
      }

      // Check if this driver is currently selected for mutation
      const isSelectedDriver = selectedDriverIds.has(driverValue.id)

      let mutatedObservations
      if (isSelectedDriver) {
        // Apply scenario-specific mutation rule using the new mathematical model
        const originalValues = originalObservation.observationValues
        const originalTimecodes = originalObservation.observationTimecodes

        const mutatedValues = mutateObservationRule(
          originalValues,
          originalTimecodes,
          driverValue.value, // target
          scenario.startDate, // t_break
          state.minDate, // t_start
          state.maxDate, // t_end
          driverValue.observationValueStart, // x_start
          driverValue.observationValueEnd, // x_end
        )

        mutatedObservations = {
          observationValues: mutatedValues,
          observationTimecodes: [...originalObservation.observationTimecodes], // Copy timecodes unchanged
        }
      } else {
        // Copy original data unchanged for non-selected drivers
        mutatedObservations = {
          observationValues: [...originalObservation.observationValues], // Create defensive copy
          observationTimecodes: [...originalObservation.observationTimecodes],
        }
      }

      return {
        ...driverValue,
        mutatedObservations,
      }
    })

    // Return updated scenario with updated driver values containing mutated observations
    return {
      ...scenario,
      driverValues: updatedDriverValues,
    }
  })

  // Update store with all updated scenarios and increment refresh counter
  useBuildScenarioStore.setState({
    scenarios: updatedScenarios,
    mutatedObservationsRefreshId: state.mutatedObservationsRefreshId + 1,
  })

  // Call remote model API after mutation is complete using throttled function
  throttledRemoteModelApiCall()
}

// ==================== REMOTE MODEL API THROTTLING ====================

// Throttle timer and tracking for remote model API calls
let remoteApiThrottleTimer = null
let lastRemoteApiCallTime = 0

/**
 * Throttled remote model API call function
 * Prevents overwhelming the API with too many requests by limiting calls
 * to the configured frequency (REMOTE_MODEL_API_THROTTLE_MS)
 *
 * This function treats every call as a fresh signal and applies its own
 * throttling logic independent of any upstream throttling (like mutation throttling)
 */
const throttledRemoteModelApiCall = () => {
  const now = Date.now()
  const timeSinceLastCall = now - lastRemoteApiCallTime

  // Clear any existing pending call since we have a new request
  if (remoteApiThrottleTimer) {
    clearTimeout(remoteApiThrottleTimer)
    remoteApiThrottleTimer = null
  }

  if (timeSinceLastCall >= REMOTE_MODEL_API_THROTTLE_MS) {
    // Enough time has passed, execute immediately
    executeRemoteModelApiCall()
    lastRemoteApiCallTime = now
  } else {
    // Too soon, schedule for later (always schedule since we cleared any existing timer)
    const remainingTime = REMOTE_MODEL_API_THROTTLE_MS - timeSinceLastCall

    remoteApiThrottleTimer = setTimeout(() => {
      executeRemoteModelApiCall()
      lastRemoteApiCallTime = Date.now()
      remoteApiThrottleTimer = null
    }, remainingTime)
  }
}

/**
 * Executes the actual remote model API call
 * Separated from throttling logic for clarity and testability
 */
const executeRemoteModelApiCall = async () => {
  try {
    await useBuildScenarioStore.getState().getRemoteModelOutput()
  } catch (error) {
    console.warn('🔄 Remote model call after mutation failed:', error.message)
    // Don't throw the error to avoid breaking the mutation flow
  }
}

// ==================== REACTIVE MUTATION SYSTEM ====================

// Throttle timer and tracking for expensive mutation operations
let lastMutationTime = 0
let pendingMutation = false

/**
 * Throttled mutation function to limit expensive operations to once every 300ms
 * Will execute immediately if enough time has passed, otherwise will schedule execution
 */
const throttledMutateObservations = () => {
  const now = Date.now()
  const timeSinceLastMutation = now - lastMutationTime

  if (timeSinceLastMutation >= 300) {
    // Enough time has passed, execute immediately
    mutateObservations()
    lastMutationTime = now
    pendingMutation = false
  } else {
    // Too soon, schedule for later if not already scheduled
    if (!pendingMutation) {
      pendingMutation = true
      const remainingTime = 300 - timeSinceLastMutation

      setTimeout(() => {
        mutateObservations()
        lastMutationTime = Date.now()
        pendingMutation = false
      }, remainingTime)
    }
  }
}

/**
 * Derived State Subscription - Reactive Mutation System
 *
 * This subscription implements an automatic mutation system that:
 * 1. Watches for changes in key state variables
 * 2. Triggers mutation processing when all required conditions are met
 * 3. Prevents infinite loops through careful state management
 *
 * Required conditions for mutation:
 * - selectedModel: A model must be selected
 * - selectedDrivers: At least one driver must be selected
 * - scenarios: At least one scenario must exist
 * - independentDriversOriginalObservations: Original data must be available
 *
 * The system uses a custom equality function to prevent unnecessary re-triggers
 * and employs setTimeout to break synchronous call chains that could cause stack overflow.
 */
useBuildScenarioStore.subscribe(
  // Selector: Define which state properties to watch
  (state) => ({
    selectedModel: state.selectedModel,
    selectedDrivers: state.selectedDrivers,
    scenarios: state.scenarios,
    independentDriversOriginalObservations:
      state.independentDriversOriginalObservations,
  }),
  // Callback: Execute when watched properties change
  ({
    selectedModel,
    selectedDrivers,
    scenarios,
    independentDriversOriginalObservations,
  }) => {
    // Validate all required conditions for mutation processing

    const hasModel = selectedModel !== null
    const hasSelectedDrivers = selectedDrivers && selectedDrivers.length > 0
    const hasScenarios = scenarios && scenarios.length > 0
    const hasOriginalObservations =
      independentDriversOriginalObservations &&
      independentDriversOriginalObservations.length > 0

    // Only proceed if all conditions are satisfied
    if (
      hasModel &&
      hasSelectedDrivers &&
      hasScenarios &&
      hasOriginalObservations
    ) {
      // Use throttled mutation to limit expensive operations to once every 300ms
      throttledMutateObservations()
    }
  },
  // Subscription options
  {
    fireImmediately: false, // Prevent initial trigger to avoid premature mutation
    equalityFn: (a, b) => {
      // Custom equality function to prevent unnecessary re-triggers
      // Only trigger when meaningful changes occur, not on every state mutation

      // Basic checks first
      if (
        a.selectedModel?.id !== b.selectedModel?.id ||
        JSON.stringify(a.selectedDrivers) !==
          JSON.stringify(b.selectedDrivers) ||
        a.scenarios.length !== b.scenarios.length ||
        a.independentDriversOriginalObservations.length !==
          b.independentDriversOriginalObservations.length
      ) {
        return false // Something changed, trigger mutation
      }

      // Deep check for scenario changes (driver values and start dates)
      for (let i = 0; i < a.scenarios.length; i++) {
        const scenarioA = a.scenarios[i]
        const scenarioB = b.scenarios[i]

        // Check if start dates changed
        if (scenarioA.startDate?.getTime() !== scenarioB.startDate?.getTime()) {
          return false // Start dates changed, trigger mutation
        }

        // Check if driver values changed
        if (scenarioA.driverValues.length !== scenarioB.driverValues.length) {
          return false // Driver values array length changed
        }

        for (let j = 0; j < scenarioA.driverValues.length; j++) {
          const driverA = scenarioA.driverValues[j]
          const driverB = scenarioB.driverValues[j]

          if (driverA.id !== driverB.id || driverA.value !== driverB.value) {
            return false // Driver values changed, trigger mutation
          }
        }
      }

      return true // No meaningful changes detected, don't trigger
    },
  },
)

/**
 * Derived State Subscription - Initial Clean Model Output System
 *
 * This subscription implements an automatic system for generating initial clean model output that:
 * 1. Watches for changes in foundational state variables
 * 2. Triggers initial clean processing when all required conditions are met
 * 3. Provides baseline predictions using original observation data (no mutations)
 *
 * Required conditions for initial clean processing:
 * - selectedModel: A model must be selected with valid ID
 * - drivers: Driver data must be available
 * - minDate/maxDate: Model timeframe must be defined
 * - independentDriversOriginalObservations: Original data must be available
 *
 * This runs once when the foundational data is ready and provides a baseline
 * for comparison with scenario-based mutations.
 */
useBuildScenarioStore.subscribe(
  // Selector: Define which state properties to watch for initial clean processing
  (state) => ({
    selectedModel: state.selectedModel,
    drivers: state.drivers,
    minDate: state.minDate,
    maxDate: state.maxDate,
    independentDriversOriginalObservations:
      state.independentDriversOriginalObservations,
    initialCleanModelOutput: state.initialCleanModelOutput, // Include to prevent re-processing
  }),
  // Callback: Execute when watched properties change
  async ({
    selectedModel,
    drivers,
    minDate,
    maxDate,
    independentDriversOriginalObservations,
    initialCleanModelOutput,
  }) => {
    // Only process if we don't already have clean output and all required data is present
    const hasModel = selectedModel !== null && selectedModel?.id
    const hasDrivers = drivers && drivers.length > 0
    const hasDateRange = minDate !== null && maxDate !== null
    const hasOriginalObservations =
      independentDriversOriginalObservations &&
      independentDriversOriginalObservations.length > 0
    const needsProcessing = initialCleanModelOutput === null

    // Only proceed if all conditions are satisfied and we haven't processed yet
    if (
      hasModel &&
      hasDrivers &&
      hasDateRange &&
      hasOriginalObservations &&
      needsProcessing
    ) {
      try {
        // Call the initial clean processing function
        await useBuildScenarioStore.getState().getInitialCleanModelOutput()
      } catch (error) {
        console.warn('🔄 Initial clean model processing failed:', error.message)
        // Don't throw the error to avoid breaking the subscription flow
      }
    }
  },
  // Subscription options
  {
    fireImmediately: false, // Prevent initial trigger to avoid premature processing
    equalityFn: (a, b) => {
      // Custom equality function to prevent unnecessary re-triggers
      // Only trigger when foundational data changes, not on every state mutation

      // Helper function to safely compare dates
      const compareDates = (dateA, dateB) => {
        // Handle null/undefined cases
        if (dateA === null && dateB === null) return true
        if (dateA === undefined && dateB === undefined) return true
        if (
          dateA === null ||
          dateA === undefined ||
          dateB === null ||
          dateB === undefined
        )
          return false

        // Convert to timestamps, handling both Date objects and strings
        let timeA, timeB

        try {
          timeA =
            dateA instanceof Date ? dateA.getTime() : new Date(dateA).getTime()
          timeB =
            dateB instanceof Date ? dateB.getTime() : new Date(dateB).getTime()

          // Check if the conversion resulted in valid timestamps
          if (isNaN(timeA) || isNaN(timeB)) return false

          return timeA === timeB
        } catch (error) {
          // If any error occurs in date conversion, consider them different
          return false
        }
      }

      return (
        a.selectedModel?.id === b.selectedModel?.id &&
        a.drivers.length === b.drivers.length &&
        compareDates(a.minDate, b.minDate) &&
        compareDates(a.maxDate, b.maxDate) &&
        a.independentDriversOriginalObservations.length ===
          b.independentDriversOriginalObservations.length &&
        (a.initialCleanModelOutput !== null) ===
          (b.initialCleanModelOutput !== null) // Compare existence, not content
      )
    },
  },
)

/**
 * Derived State Subscription - Submit Readiness System
 *
 * This subscription monitors lastRemoteModelOutput changes and checks if any scenarios
 * have been modified from their initial values to determine if the submit button should be enabled.
 *
 * Checks for modifications:
 * - Driver values different from their original end values
 * - Start dates different from the initial start date
 *
 * Sets isReadyForSubmitFlag to true if any modifications are detected, false otherwise.
 */
useBuildScenarioStore.subscribe(
  // Selector: Watch for lastRemoteModelOutput changes (ANY change triggers this)
  (state) => state.lastRemoteModelOutput,
  // Callback: Check if scenarios have been modified whenever lastRemoteModelOutput changes
  (lastRemoteModelOutput) => {
    const state = useBuildScenarioStore.getState()

    // Early return if no scenarios exist
    if (!state.scenarios || state.scenarios.length === 0) {
      useBuildScenarioStore.setState({ isReadyForSubmitFlag: false })
      return
    }

    let hasModifications = false
    const initialStartDate = state.getInitStartDate()

    // Check each scenario for modifications
    for (const scenario of state.scenarios) {
      // Check if start date has been modified
      if (initialStartDate && scenario.startDate) {
        if (scenario.startDate.getTime() !== initialStartDate.getTime()) {
          hasModifications = true
          break
        }
      }

      // Check each driver value for modifications
      for (const driverValue of scenario.driverValues) {
        const defaultValues = state.getDriverDefaultValues(driverValue.id)

        // Compare current value with original end value
        if (
          defaultValues.observationValueEnd &&
          driverValue.value !== defaultValues.observationValueEnd
        ) {
          hasModifications = true
          break
        }
      }

      if (hasModifications) break
    }

    // Update the flag and log the result
    useBuildScenarioStore.setState({ isReadyForSubmitFlag: hasModifications })
  },
  // Subscription options
  {
    fireImmediately: false, // Don't trigger on initial subscription
  },
)

/**
 * Hook to get a specific scenario's start date with optimized re-rendering
 * Uses custom equality to prevent unnecessary re-renders when only the date changes
 *
 * @param {number} scenarioId - ID of the scenario to watch
 * @returns {Date|null} The scenario's start date or null if not found
 */
export const useScenarioStartDate = (scenarioId) => {
  return useBuildScenarioStore(
    (state) => {
      const scenario = state.scenarios.find((s) => s.id === scenarioId)
      return scenario?.startDate || null
    },
    (a, b) => a?.getTime() === b?.getTime(), // Compare dates by timestamp
  )
}

/**
 * Hook to get a specific driver's value within a specific scenario
 * Provides fine-grained reactivity for individual driver value changes
 *
 * @param {number} scenarioId - ID of the scenario to watch
 * @param {number} driverId - ID of the driver to watch
 * @returns {Object} The driver value object in the scenario (defaults to { id: driverId, value: 0 })
 */
export const useScenarioDriverValue = (scenarioId, driverId) => {
  return useBuildScenarioStore(
    (state) => {
      const scenario = state.scenarios.find((s) => s.id === scenarioId)
      const driver = scenario?.driverValues?.find((d) => d.id === driverId)
      return driver || { id: driverId, value: 0 }
    },
    (a, b) => JSON.stringify(a) === JSON.stringify(b), // Deep equality for object comparison
  )
}

/**
 * Hook to detect scenario structure changes (excluding driver values and start dates)
 * Useful for components that only need to re-render when scenarios are added/removed/renamed
 * but not when individual driver values change
 *
 * This provides performance optimization by avoiding re-renders on frequent value updates
 *
 * @returns {Array} Array of scenarios
 */
export const useScenarioChangedExceptDriversAndStartValue = () => {
  return useBuildScenarioStore(
    (state) => state.scenarios,
    (prevScenarios, nextScenarios) => {
      // Custom equality function: return true if scenarios are structurally equal
      if (prevScenarios.length !== nextScenarios.length) return false

      // Check if IDs, names, and isNew flags are the same (ignore driver values and dates)
      return prevScenarios.every((scenario, index) => {
        const nextScenario = nextScenarios[index]
        return (
          nextScenario &&
          scenario.id === nextScenario.id &&
          scenario.name === nextScenario.name &&
          scenario.isNew === nextScenario.isNew
        )
      })
    },
  )
}

/**
 * Hook to reactively listen to mutation processing completion
 * Components can use this to trigger re-renders when observation mutations are complete
 *
 * The refresh ID increments each time the mutation system processes observations,
 * enabling components (like charts) to update their visualizations accordingly
 *
 * @returns {number} Current mutation refresh ID
 */
export const useMutatedObservationsRefresh = () => {
  return useBuildScenarioStore(
    (state) => state.mutatedObservationsRefreshId,
    (a, b) => a === b, // Simple numeric equality
  )
}

/**
 * Hook to get mutated observations for a specific scenario
 * This provides scenario-specific observation data for visualization
 *
 * @param {number} scenarioId - ID of the scenario to get observations for
 * @returns {Array} Array of mutated observation objects for the scenario
 */
export const useScenarioMutatedObservations = (scenarioId) => {
  return useBuildScenarioStore(
    (state) => {
      const scenario = state.scenarios.find((s) => s.id === scenarioId)
      if (!scenario) {
        return []
      }

      // Transform driver values with mutated observations into the expected format
      const result = scenario.driverValues.map((driver) => ({
        driverId: driver.id,
        observationValues: driver.mutatedObservations?.observationValues || [],
        observationTimecodes:
          driver.mutatedObservations?.observationTimecodes || [],
      }))

      return result
    },
    (a, b) => JSON.stringify(a) === JSON.stringify(b), // Deep equality for observation arrays
  )
}

/**
 * Hook to get mutated observations for a specific driver within a specific scenario
 * This provides driver-specific observation data for detailed analysis
 *
 * @param {number} scenarioId - ID of the scenario
 * @param {number} driverId - ID of the driver
 * @returns {Object} Object with observationValues and observationTimecodes arrays
 */
export const useScenarioDriverMutatedObservations = (scenarioId, driverId) => {
  return useBuildScenarioStore(
    (state) => {
      const scenario = state.scenarios.find((s) => s.id === scenarioId)
      const driver = scenario?.driverValues?.find((d) => d.id === driverId)
      return (
        driver?.mutatedObservations || {
          observationValues: [],
          observationTimecodes: [],
        }
      )
    },
    (a, b) => JSON.stringify(a) === JSON.stringify(b), // Deep equality for observation objects
  )
}

/**
 * Hook to get remote model output state
 * Provides access to the results from the remote XGBoost API
 *
 * @returns {Object} Object containing output, loading state, and error state
 */
export const useRemoteModelOutput = () => {
  return useBuildScenarioStore(
    (state) => ({
      output: state.remoteModelOutput,
      lastOutput: state.lastRemoteModelOutput, // Always available after first successful call
      loading: state.remoteModelLoading,
      error: state.remoteModelError,
    }),
    (a, b) => JSON.stringify(a) === JSON.stringify(b), // Deep equality for state object
  )
}

/**
 * Hook to get just the remote model loading state
 * Useful for showing loading indicators
 *
 * @returns {boolean} True if remote model API call is in progress
 */
export const useRemoteModelLoading = () => {
  return useBuildScenarioStore(
    (state) => state.remoteModelLoading,
    (a, b) => a === b, // Simple boolean equality
  )
}

/**
 * Hook to manually trigger remote model processing
 * Provides access to the getRemoteModelOutput function
 *
 * @returns {Function} Function to call remote model API
 */
export const useGetRemoteModelOutput = () => {
  return useBuildScenarioStore((state) => state.getRemoteModelOutput)
}

/**
 * Hook to get the last successful remote model output
 * This will persist data even when new API calls are in progress
 *
 * @returns {Object|null} Last successful remote model output or null
 */
export const useLastRemoteModelOutput = () => {
  return useBuildScenarioStore(
    (state) => state.lastRemoteModelOutput,
    (a, b) => JSON.stringify(a) === JSON.stringify(b), // Deep equality for output object
  )
}

/**
 * Hook to get initial clean model output state
 * Provides access to the baseline prediction results using original observation data
 *
 * @returns {Object} Object containing output, loading state, and error state
 */
export const useInitialCleanModelOutput = () => {
  return useBuildScenarioStore(
    (state) => ({
      output: state.initialCleanModelOutput,
      loading: state.initialCleanModelLoading,
      error: state.initialCleanModelError,
    }),
    (a, b) => JSON.stringify(a) === JSON.stringify(b), // Deep equality for complete state object
  )
}

/**
 * Hook to manually trigger initial clean model processing
 * Provides access to the getInitialCleanModelOutput function
 *
 * @returns {Function} Function to call initial clean model processing
 */
export const useGetInitialCleanModelOutput = () => {
  return useBuildScenarioStore((state) => state.getInitialCleanModelOutput)
}

/**
 * Hook to listen to scenario name changes
 * Triggers when any scenario name has been changed
 *
 * @returns {Array} Array of scenario names mapped to their IDs
 */
export const useScenarioNameChanges = () => {
  return useBuildScenarioStore(
    (state) =>
      state.scenarios.map((scenario) => ({
        id: scenario.id,
        name: scenario.name,
      })),
    (prevNames, nextNames) => {
      // Custom equality function: return true if names are exactly the same
      if (prevNames.length !== nextNames.length) return false

      // Check if any scenario name has changed
      return prevNames.every((prevScenario, index) => {
        const nextScenario = nextNames[index]
        return (
          prevScenario.id === nextScenario.id &&
          prevScenario.name === nextScenario.name
        )
      })
    },
  )
}

export default useBuildScenarioStore
